import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { Button } from '../../../forms/Button';
import type { PinPlacementConfig } from '../types';

interface MapPinPlacementBarProps extends PinPlacementConfig {
  /** Where the person has put the marker; null until they tap or drag. */
  placed: { lat: number; lng: number } | null;
}

/**
 * The bar under a map in pin-placement mode. Save is disabled until the person
 * has placed the marker themselves: the map opens on the street address, and
 * the address must never be saved as the unit's pin by default.
 */
export function MapPinPlacementBar({ placed, onSave }: MapPinPlacementBarProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!placed) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(placed);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The pin was not saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-t border-slate-200">
      <MapPin className="w-4 h-4 text-[#007ACC] shrink-0" />
      <p className="flex-1 min-w-0 text-xs text-slate-600">
        {placed
          ? <>Pin at <span className="font-mono tabular-nums">{placed.lat.toFixed(6)}, {placed.lng.toFixed(6)}</span></>
          : 'Tap the map or drag the marker to where the unit is.'}
      </p>
      <Button size="sm" disabled={!placed || saving} onClick={save}>
        {saving ? 'Saving…' : 'Save pin here'}
      </Button>
      {error && <p role="alert" className="basis-full text-xs text-red-700">{error}</p>}
    </div>
  );
}
